import { useState, useMemo, useEffect, useCallback } from 'react';
import { DataGrid, KeenIcon, Modal } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import usePagination from '@/hooks/usePagination';
import {
  useGetAllBookingsQuery,
  useLazyExportBookingsCsvQuery,
  useUpdateBookingStatusMutation,
  useGetBookingNoShowSummaryMutation,
  useMarkBookingNoShowMutation,
} from '@/services/BookingService';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import {
  IBookingData,
  BookingStatus,
  BookingNoShowSummary,
} from '@/types/booking-management.types';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { formatFinnishCurrency } from '@/utils';
import { RefundMode } from '@/constant';
import { handleError } from '@/utils/BaseQuery';
import { formatDisplayDate } from '@/utils/Date';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { HarborCapability } from '@/constant/harbour';
import { UserRoles } from '@/constant';
import { useCurrentUserRoles } from '@/utils/userRole';

export interface HarborOption {
  value: string;
  label: string;
}

export interface BookingsListingProps {
  handleViewClick?: (bookingId: string) => void;
  harborId?: string;
  showExportControls?: boolean;
}

const BookingsListing = (props: BookingsListingProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const { hasCapability } = usePlanPermissions();
  const roles = useCurrentUserRoles();
  const isAdmin = roles.includes(UserRoles.ADMIN);
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    bookingId: string | null;
    action: 'pending' | 'cancel' | null;
    reason?: string;
  }>({ isOpen: false, bookingId: null, action: null });
  const [noShowState, setNoShowState] = useState<{
    isOpen: boolean;
    bookingId: string | null;
    mode: RefundMode | null;
    summary: BookingNoShowSummary | null;
    note: string;
    step: 'choice' | 'summary';
  }>({
    isOpen: false,
    bookingId: null,
    mode: null,
    summary: null,
    note: '',
    step: 'choice',
  });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [guestFilter, setGuestFilter] = useState<
    'all' | 'guest_unclaimed' | 'guest_claimed'
  >('all');
  const [harborOptions, setHarborOptions] = useState<HarborOption[]>([]);
  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [exportBookingsCsv, { isLoading: isExportingCsv }] =
    useLazyExportBookingsCsvQuery();
  const [selectedHarbor, setSelectedHarbor] = useState<string>(
    props.harborId ||
      (selectedHarborId && location.pathname === '/harbors/details'
        ? selectedHarborId
        : '')
  );

  const [harborPagination, setHarborPagination] = useState({
    page: 1,
    limit: 10,
  });

  const { data: harborsData, isLoading: isHarborsLoading } = useGetHarborsQuery(
    {}
  );

  const getStatusEnum = (filter: string): number | undefined => {
    const statusMap: { [key: string]: number } = {
      pending: BookingStatus.PENDING, // 1
      confirmed: BookingStatus.CONFIRMED, // 2
      cancelled: BookingStatus.CANCELLED, // 3
      completed: BookingStatus.COMPLETED, // 4
      noshow: BookingStatus.PENDING_CONFIRMATION, // 5
    };
    return filter !== 'all' ? statusMap[filter] : undefined;
  };

  const hasCustomStartDate =
    tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid();
  const hasCustomEndDate =
    tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid();
  const formattedStartDate = hasCustomStartDate
    ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
    : undefined;
  const formattedEndDate = hasCustomEndDate
    ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
    : undefined;

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    status: getStatusEnum(statusFilter),
    harbor_id: selectedHarbor || undefined,
    start_date: formattedStartDate,
    end_date: formattedEndDate,
    from: formattedStartDate,
    to: formattedEndDate,
    guest_status: guestFilter === 'all' ? undefined : guestFilter,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetAllBookingsQuery(queryParams);

  const [updateBookingStatus, { isLoading: isUpdating }] =
    useUpdateBookingStatusMutation();

  const [getBookingNoShowSummary, { isLoading: isLoadingNoShowSummary }] =
    useGetBookingNoShowSummaryMutation();
  const [markBookingNoShow, { isLoading: isMarkingNoShow }] =
    useMarkBookingNoShowMutation();

  const isNoShowProcessing = isLoadingNoShowSummary || isMarkingNoShow;

  const getApiErrorMessage = useCallback((error: unknown) => {
    const normalizeErrorMessage = (message?: string) => {
      if (!message) return undefined;
      return message.replace(/^message\.message\./, '');
    };

    if (!error || typeof error !== 'object') {
      return undefined;
    }

    const rtkError = error as {
      data?: unknown;
      message?: string;
    };

    if (rtkError.data && typeof rtkError.data === 'object') {
      const errorData = rtkError.data as {
        message?: string;
        data?: { message?: string };
        errors?: Record<string, string | string[]>;
      };

      if (
        errorData.data &&
        typeof errorData.data === 'object' &&
        typeof errorData.data.message === 'string'
      ) {
        return normalizeErrorMessage(errorData.data.message);
      }

      if (typeof errorData.message === 'string') {
        return normalizeErrorMessage(errorData.message);
      }

      if (errorData.errors && typeof errorData.errors === 'object') {
        const validationMessages = Object.values(errorData.errors).flatMap(
          (value) => (Array.isArray(value) ? value : [value])
        );

        if (validationMessages.length > 0) {
          return validationMessages.join('\n');
        }
      }
    }

    if (typeof rtkError.message === 'string') {
      return normalizeErrorMessage(rtkError.message);
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.total || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (harborsData?.data) {
      const options = harborsData.data.map((harbor) => ({
        value: harbor.id,
        label: harbor.name,
      }));
      setHarborOptions(options);
    }
  }, [harborsData]);

  useEffect(() => {
    if (props.harborId) {
      setSelectedHarbor(props.harborId);
      return;
    }

    if (selectedHarborId && location.pathname === '/harbors/details') {
      setSelectedHarbor(selectedHarborId);
    }
  }, [props.harborId, selectedHarborId]);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [
    statusFilter,
    guestFilter,
    debouncedValue,
    selectedHarbor,
    tempStartDate,
    tempEndDate,
    harborPagination,
    pagination,
    refetch,
  ]);

  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((booking) => ({
      id: booking.id,
      user: {
        id: booking.user?.id,
        name: booking.user?.name,
        email: booking.user?.email,
      },
      berth: {
        id: booking.berth?.id,
        berth_number: booking.berth?.berth_number,
      },
      start_date: booking.start_date,
      end_date: booking.end_date,
      total_price: booking.total_price,
      payment_type: booking.payment_type,
      status: booking.status,
      special_requests: booking.special_requests || null,
      cancellation_reason: booking.cancellation_reason || null,
      isConflicting: booking.isConflicting,
      mediation_case: booking.mediation_case || null,
      booking_addons: booking.booking_addons || [],
      created_at: booking.created_at,
      booking_reference: booking.booking_reference ?? null,
      is_guest_booking: booking.is_guest_booking ?? false,
      is_claimed: booking.is_claimed ?? false,
      claimed_at: booking.claimed_at ?? null,
      guest_details: booking.guest_details ?? null,
    }));
  }, [data]);

  const hasBookingStarted = useCallback((startDate: string) => {
    if (!startDate) return false;
    const start = moment(startDate);
    if (!start.isValid()) return false;
    return start.isSameOrBefore(moment());
  }, []);

  const { handleViewClick: propHandleViewClick } = props;
  const canExportCsv =
    props.showExportControls &&
    (isAdmin || hasCapability(HarborCapability.BOOKINGS_MANAGEMENT_EXPORT_CSV));

  const exportFileName = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (formattedStartDate && formattedEndDate) {
      return `harbor_bookings_${formattedStartDate}_${formattedEndDate}.csv`;
    }

    return `harbor_bookings_${today}.csv`;
  }, [formattedStartDate, formattedEndDate]);

  const handleActionClick = (
    bookingId: string,
    action: 'pending' | 'cancel'
  ) => {
    setConfirmActionState({ isOpen: true, bookingId, action });
  };

  const handleViewClick = useCallback(
    (bookingId: string) => {
      if (propHandleViewClick) {
        propHandleViewClick(bookingId);
      } else {
        navigate(`/booking-management/${bookingId}`);
      }
    },
    [propHandleViewClick, navigate]
  );

  const handleEditClick = useCallback(
    (bookingId: string) => {
      navigate(`/booking-management/${bookingId}/edit`);
    },
    [navigate]
  );

  const handleNoShowClick = useCallback(
    (bookingId: string) => {
      setNoShowState({
        isOpen: true,
        bookingId,
        mode: null,
        summary: null,
        note: '',
        step: 'choice',
      });
    },
    [setNoShowState]
  );

  const handleCloseNoShowModal = () => {
    setNoShowState({
      isOpen: false,
      bookingId: null,
      mode: null,
      summary: null,
      note: '',
      step: 'choice',
    });
  };

  const handleNoShowNoteChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setNoShowState((prev) => ({
      ...prev,
      note: value,
    }));
  };

  const handleNoShowRefundOption = async () => {
    if (!noShowState.bookingId) return;
    try {
      const response = await getBookingNoShowSummary(
        noShowState.bookingId
      ).unwrap();

      if (!response.status) {
        if (response.message) {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
        return;
      }

      setNoShowState((prev) => ({
        ...prev,
        mode: RefundMode.REFUND_AS_POLICY,
        summary: response.data,
        step: 'summary',
      }));
    } catch (error) {
      console.error('Failed to fetch no-show refund summary:', error);
      const errorMessage = getApiErrorMessage(error);
      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleNoShowNoRefundOption = async () => {
    if (!noShowState.bookingId) return;
    try {
      const response = await markBookingNoShow({
        id: noShowState.bookingId,
        data: {
          mode: RefundMode.NO_REFUND,
          note: noShowState.note.trim(),
        },
      }).unwrap();

      if (!response.status) {
        if (response.message) {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
        return;
      }

      if (typeof refetch === 'function') {
        refetch();
      }
      handleCloseNoShowModal();
    } catch (error) {
      console.error('Failed to mark booking as no show (no refund):', error);
      const errorMessage = getApiErrorMessage(error);
      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleConfirmRefundNoShow = async () => {
    if (!noShowState.bookingId) return;
    try {
      const response = await markBookingNoShow({
        id: noShowState.bookingId,
        data: {
          mode: RefundMode.REFUND_AS_POLICY,
          note: noShowState.note.trim(),
        },
      }).unwrap();

      if (!response.status) {
        if (response.message) {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
        return;
      }

      if (typeof refetch === 'function') {
        refetch();
      }
      handleCloseNoShowModal();
    } catch (error) {
      console.log('Error details in catch:', error);
      console.error('Failed to mark booking as no show (refund):', error);
      const errorMessage = getApiErrorMessage(error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleConfirmAction = async () => {
    if (confirmActionState.bookingId && confirmActionState.action) {
      const updateData = {
        status:
          confirmActionState.action === 'pending'
            ? BookingStatus.PENDING
            : BookingStatus.CANCELLED,
        cancellation_reason:
          confirmActionState.action === 'cancel'
            ? confirmActionState.reason
            : undefined,
      };

      try {
        await updateBookingStatus({
          id: confirmActionState.bookingId,
          update: updateData,
        }).unwrap();
        if (typeof refetch === 'function') {
          refetch();
        }
        setConfirmActionState({
          isOpen: false,
          bookingId: null,
          action: null,
          reason: undefined,
        });
      } catch (error) {
        console.error('Failed to update booking status:', error);
        handleError(
          error as { status?: number; data?: unknown; message?: string }
        );
        setConfirmActionState({
          isOpen: false,
          bookingId: null,
          action: null,
          reason: undefined,
        });
      }
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmActionState((prev) => ({ ...prev, reason: e.target.value }));
  };

  const handleHarborChange = (selectedOption: HarborOption | null) => {
    const harborId = selectedOption ? selectedOption.value : '';
    setSelectedHarbor(harborId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleDownloadExport = useCallback(
    async (
      payload:
        | Blob
        | string
        | { fileUrl?: string; url?: string; data?: { fileUrl?: string } }
    ) => {
      if (payload instanceof Blob) {
        if (payload.type?.includes('application/json')) {
          const text = await payload.text();
          const parsed = JSON.parse(text) as {
            fileUrl?: string;
            url?: string;
            data?: { fileUrl?: string };
          };
          const remoteUrl =
            parsed.fileUrl || parsed.url || parsed.data?.fileUrl;

          if (remoteUrl) {
            window.open(remoteUrl, '_blank', 'noopener,noreferrer');
            return;
          }
        }

        const url = window.URL.createObjectURL(payload);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', exportFileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }

      if (typeof payload === 'string') {
        window.open(payload, '_blank', 'noopener,noreferrer');
        return;
      }

      const remoteUrl = payload.fileUrl || payload.url || payload.data?.fileUrl;
      if (remoteUrl) {
        window.open(remoteUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      throw new Error('Unsupported export response');
    },
    [exportFileName]
  );

  const handleExportCsv = async () => {
    if (!selectedHarbor) {
      enqueueSnackbar('Please select a harbor before exporting CSV', {
        variant: 'warning',
      });
      return;
    }

    try {
      const exportResponse = await exportBookingsCsv({
        harbor_id: selectedHarbor,
        status: getStatusEnum(statusFilter),
        search: debouncedValue || undefined,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        from: formattedStartDate,
        to: formattedEndDate,
      }).unwrap();

      await handleDownloadExport(exportResponse);
      enqueueSnackbar('Bookings CSV exported successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to export bookings CSV:', error);
      const errorMessage = getApiErrorMessage(error);
      enqueueSnackbar(errorMessage || 'Failed to export bookings CSV', {
        variant: 'error',
      });
    }
  };

  const resetFilters = () => {
    resetSearch();
    setStatusFilter('all');
    setGuestFilter('all');
    setSelectedHarbor(props.harborId || selectedHarborId || '');
    setTempStartDate('');
    setTempEndDate('');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IBookingData) => row.user.name,
        id: 'user_name',
        header: 'User',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span className="text-sm font-medium text-gray-900">
            {row.original.user.name || row.original.user.email || '-'}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IBookingData) => row.berth.berth_number,
        id: 'berth_number',
        header: 'Berth',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.berth.berth_number || '-'}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IBookingData) => row.start_date,
        id: 'start_date',
        header: 'Start Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.start_date.split('T')[0]))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IBookingData) => row.end_date,
        id: 'end_date',
        header: 'End Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.end_date.split('T')[0]))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IBookingData) => row.total_price,
        id: 'total_price',
        header: 'Total Price',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span className="text-gray-800 font-normal">
            {formatFinnishCurrency(row.original.total_price)}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IBookingData) => row.payment_type,
        id: 'payment_type',
        header: 'Payment Type',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.payment_type === 1
              ? 'Credit Card'
              : row.original.payment_type === 2
                ? 'Bank Transfer'
                : 'Other'}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IBookingData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span
            data-testid={`status-${row.original.id}`}
            className={`text-sm font-medium ${
              row.original.status === BookingStatus.CONFIRMED
                ? 'text-green-600'
                : row.original.status === BookingStatus.CANCELLED
                  ? 'text-red-600'
                  : row.original.status === BookingStatus.COMPLETED
                    ? 'text-blue-600'
                    : row.original.status === BookingStatus.PENDING_CONFIRMATION
                      ? 'text-gray-600'
                      : 'text-yellow-600'
            }`}
          >
            {row.original.status === BookingStatus.PENDING
              ? 'Payment Pending'
              : row.original.status === BookingStatus.CONFIRMED
                ? 'Confirmed'
                : row.original.status === BookingStatus.CANCELLED
                  ? 'Cancelled'
                  : row.original.status === BookingStatus.COMPLETED
                    ? 'Completed'
                    : row.original.status === BookingStatus.PENDING_CONFIRMATION
                      ? 'Pending Confirmation'
                      : 'Payment Pending'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IBookingData) => row.mediation_case?.status,
        id: 'mediation_status',
        header: 'Mediation',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => {
          const mediationStatus = row.original.mediation_case?.status;
          const isConflicting = row.original.isConflicting;

          if (!mediationStatus && !isConflicting) {
            return <span className="text-gray-400">-</span>;
          }

          const label = mediationStatus
            ? mediationStatus.replace('_', ' ')
            : 'Conflict flagged';
          const isActionable =
            mediationStatus === 'OPEN' ||
            mediationStatus === 'AWAITING_MANAGER' ||
            mediationStatus === 'AWAITING_BOATER_B' ||
            mediationStatus === 'AWAITING_BOATER_A';

          return (
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                isActionable
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {label}
            </span>
          );
        },
        meta: { className: 'min-w-[170px]' },
      },
      {
        accessor: (row: IBookingData) => row.is_guest_booking,
        id: 'guest_claim_status',
        header: 'Guest/Claim',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => {
          if (!row.original.is_guest_booking) {
            return <span className="text-gray-400">-</span>;
          }

          return (
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                row.original.is_claimed
                  ? 'bg-green-100 text-green-700'
                  : 'bg-orange-100 text-orange-700'
              }`}
            >
              {row.original.is_claimed ? 'Claimed' : 'Guest (Unclaimed)'}
            </span>
          );
        },
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IBookingData) => row.special_requests,
        id: 'special_requests',
        header: 'Special Requests',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.special_requests || '-'}
          </span>
        ),
        meta: { className: 'min-w-[250px]' },
      },
      {
        accessor: (row: IBookingData) => row.created_at,
        id: 'created_at',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.created_at))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBookingData } }) => (
          <div className="flex items-center gap-2">
            {hasPermission('berth_booking_management_read') && (
              <button
                type="button"
                onClick={() => handleViewClick(row.original.id)}
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="View"
                aria-label={`View ${row.original.user.name}'s booking`}
                disabled={isUpdating}
              >
                <KeenIcon type="filled" icon="eye" className="text-blue-500" />
              </button>
            )}
            {(row.original.status === BookingStatus.PENDING ||
              row.original.status === BookingStatus.PENDING_CONFIRMATION) &&
              hasPermission('berth_booking_management_update') && (
                <button
                  type="button"
                  onClick={() => handleEditClick(row.original.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  title="Edit"
                  aria-label={`Edit ${row.original.user.name}'s booking`}
                  disabled={isUpdating}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-yellow-500"
                  />
                </button>
              )}

            {row.original.status === BookingStatus.PENDING_CONFIRMATION &&
              hasPermission('berth_booking_management_update') && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      handleActionClick(row.original.id, 'pending')
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-green-500"
                    title="Confirm"
                    aria-label={`Confirm ${row.original.user.name}'s booking`}
                    disabled={isUpdating}
                  >
                    <KeenIcon
                      type="filled"
                      icon="check"
                      className="text-green-500"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleActionClick(row.original.id, 'cancel')}
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Cancel"
                    aria-label={`Cancel ${row.original.user.name}'s booking`}
                    disabled={isUpdating}
                  >
                    <KeenIcon
                      type="filled"
                      icon="cross"
                      className="text-red-500"
                    />
                  </button>
                </>
              )}
            {row.original.status === BookingStatus.CONFIRMED &&
              hasPermission('berth_booking_management_update') &&
              hasBookingStarted(row.original.start_date) && (
                <button
                  type="button"
                  onClick={() => handleNoShowClick(row.original.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500"
                  title="Mark as No Show"
                  aria-label={`Mark ${row.original.user.name}'s booking as no show`}
                  disabled={isUpdating || isNoShowProcessing}
                >
                  <KeenIcon
                    type="filled"
                    icon="shield-slash"
                    className="text-red-500"
                  />
                </button>
              )}
          </div>
        ),
        meta: { className: 'w-[200px]' },
      },
    ],
    [
      handleViewClick,
      handleEditClick,
      hasPermission,
      hasBookingStarted,
      handleNoShowClick,
      isUpdating,
      isNoShowProcessing,
    ]
  );

  return (
    <div className="berth-listings card card-grid w-full">
      <div className="px-6 pt-5">
        <h1 className="text-base font-semibold text-gray-900">
          Booking Management
        </h1>
      </div>
      <div className="card-header berth-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="berth-header-left flex items-center gap-2">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-bookings"
              aria-label="Search bookings by user, berth, or date"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-bookings"
                placeholder="Search bookings"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search bookings by user, berth, or date"
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter bookings by status"
              disabled={isLoading || isFetching || isUpdating}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
              <option value="noshow">Pending Confirmation</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <select
              id="guest-claim-filter"
              value={guestFilter}
              onChange={(e) => {
                setGuestFilter(
                  e.target.value as 'all' | 'guest_unclaimed' | 'guest_claimed'
                );
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter bookings by guest/claim status"
              disabled={isLoading || isFetching || isUpdating}
            >
              <option value="all">All Bookings</option>
              <option value="guest_unclaimed">Guest (Unclaimed)</option>
              <option value="guest_claimed">Guest (Claimed)</option>
            </select>
          </div>
          <div className="flex-shrink-0 w-full sm:w-64">
            <Select
              id="harbor-filter"
              options={harborOptions}
              value={
                harborOptions.find(
                  (option) => option.value === selectedHarbor
                ) || null
              }
              onChange={handleHarborChange}
              isDisabled={
                (!!props.harborId && props.showExportControls) ||
                (!!selectedHarborId &&
                  location.pathname === '/harbors/details') ||
                isLoading ||
                isFetching ||
                isUpdating ||
                isHarborsLoading
              }
              placeholder={
                isHarborsLoading ? 'Loading harbors...' : 'Select harbor'
              }
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: '0.5rem',
                  borderColor: '#d1d5db',
                  minHeight: '2.5rem',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: '#93c5fd',
                  },
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 50,
                  marginTop: '0.5rem',
                  borderRadius: '0.375rem',
                  boxShadow:
                    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                }),
                option: (base, state) => ({
                  ...base,
                  fontSize: '0.875rem',
                  backgroundColor: state.isSelected
                    ? '#e0f2fe'
                    : state.isFocused
                      ? '#f3f4f6'
                      : 'white',
                  color: '#1f2937',
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#9ca3af',
                }),
              }}
              aria-label="Filter bookings by harbor"
            />
            {(harborsData?.total_pages || 0) > 1 && (
              <div className="flex justify-between mt-1">
                <button
                  disabled={harborPagination.page === 1}
                  onClick={() =>
                    setHarborPagination((prev) => ({
                      ...prev,
                      page: prev.page - 1,
                    }))
                  }
                  className="btn btn-xs"
                >
                  Previous
                </button>
                <span className="text-xs">
                  Page {harborPagination.page} of{' '}
                  {harborsData?.total_pages || 1}
                </span>
                <button
                  disabled={
                    harborPagination.page >= (harborsData?.total_pages || 1)
                  }
                  onClick={() =>
                    setHarborPagination((prev) => ({
                      ...prev,
                      page: prev.page + 1,
                    }))
                  }
                  className="btn btn-xs"
                >
                  Next
                </button>
              </div>
            )}
          </div>
          {props.showExportControls && (
            <div className="flex-shrink-0">
              <DateRangePicker
                startDateName="booking-start-date"
                endDateName="booking-end-date"
                isMandatory={false}
                value={[tempStartDate, tempEndDate]}
                onChange={([newStartDate, newEndDate]) => {
                  handleDateRangeChange(
                    newStartDate as string,
                    newEndDate as string
                  );
                }}
                allowPastDates={true}
                allowFutureDates={true}
                className="w-full sm:w-64 h-8 sm:h-10 text-sm"
              />
            </div>
          )}
        </div>
        <div className="berth-header-right flex items-center gap-2">
          {(searchTerm.trim() !== '' ||
            statusFilter !== 'all' ||
            guestFilter !== 'all' ||
            !!tempStartDate ||
            !!tempEndDate) && (
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          )}
          {canExportCsv ? (
            <button
              type="button"
              className={`btn btn-sm btn-primary flex items-center gap-2 h-8 sm:h-10 ${
                isExportingCsv ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={handleExportCsv}
              disabled={isExportingCsv || !selectedHarbor}
            >
              {isExportingCsv ? 'Exporting...' : 'Export CSV'}
            </button>
          ) : null}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || isUpdating ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-600" role="alert">
            Error loading data. Please try again.
          </div>
        ) : (
          <>
            {!isLoading && !isError && filteredData.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-500">No Data available</p>
              </div>
            )}
            {!isLoading && !isError && filteredData.length > 0 && (
              <div className="berth-grid-wrapper">
                <DataGrid
                  data-testid="bookings-data-grid"
                  columns={columns}
                  data={filteredData}
                  rowSelect={false}
                  loading={isLoading || isFetching || isUpdating}
                  pagination={{
                    page: pagination.pageIndex,
                    size: pagination.pageSize,
                    totalItems: data?.total || totalItems,
                    totalPages: data?.totalPages || totalPages,
                    setPagination,
                    info: `{from} - {to} of {count}`,
                    sizes: [5, 10, 25, 50, 100],
                    sizesLabel: 'Show',
                    sizesDescription: 'per page',
                  }}
                  sorting={[{ id: 'user_name', desc: false }]}
                  serverSide={true}
                  hasActiveFilters={
                    !!searchTerm ||
                    statusFilter !== 'all' ||
            guestFilter !== 'all' ||
                    (!!selectedHarbor && selectedHarbor !== props.harborId) ||
                    !!tempStartDate ||
                    !!tempEndDate
                  }
                  aria-label="Bookings table"
                />
              </div>
            )}
          </>
        )}
        <ConfirmationDialog
          open={confirmActionState.isOpen}
          onClose={() =>
            setConfirmActionState({
              isOpen: false,
              bookingId: null,
              action: null,
              reason: undefined,
            })
          }
          onConfirm={handleConfirmAction}
          message={
            confirmActionState.action === 'cancel' ? (
              <>
                Are you sure you want to cancel this booking?
                <input
                  type="text"
                  placeholder="Cancellation reason (optional)"
                  value={confirmActionState.reason || ''}
                  onChange={handleReasonChange}
                  className="mt-2 bg-gray-100 w-full p-2 border rounded"
                  disabled={isUpdating}
                />
              </>
            ) : (
              `Are you sure you want to approve this Booking?`
            )
          }
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="action-confirmation-title"
          aria-describedby="action-confirmation-description"
        />
        <Modal
          open={noShowState.isOpen}
          onClose={handleCloseNoShowModal}
          className="!flex items-center justify-center"
          aria-labelledby="no-show-dialog-title"
          aria-describedby="no-show-dialog-description"
        >
          <div
            className="bg-gray-100 p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar rounded-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="no-show-dialog-title"
            aria-describedby="no-show-dialog-description"
          >
            <h2
              id="no-show-dialog-title"
              className="text-lg font-medium text-gray-900 mb-4"
            >
              Mark Booking as No Show
            </h2>
            <div
              id="no-show-dialog-description"
              className="text-sm text-gray-700 mb-4"
            >
              {noShowState.step === 'choice' ? (
                <p>
                  Select how you want to handle this no-show booking. You can
                  apply a refund according to the policy or mark it with no
                  refund.
                </p>
              ) : (
                <>
                  <p className="mb-3">
                    Review the refund summary calculated from the no-show
                    policy.
                  </p>
                  {noShowState.summary && (
                    <div className="bg-gray-100 rounded-md border border-gray-200 p-3 text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Net paid</span>
                        <span className="font-medium">
                          {formatFinnishCurrency(noShowState.summary.net_paid)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Penalty amount</span>
                        <span className="font-medium">
                          {formatFinnishCurrency(
                            noShowState.summary.penalty_amount
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total refund</span>
                        <span className="font-medium">
                          {formatFinnishCurrency(
                            noShowState.summary.total_refund
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="mb-4">
              <label
                htmlFor="no-show-note"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note (optional)
              </label>
              <textarea
                id="no-show-note"
                value={noShowState.note}
                onChange={handleNoShowNoteChange}
                rows={3}
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Add an internal note about this no-show..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseNoShowModal}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isNoShowProcessing}
              >
                Cancel
              </button>
              {noShowState.step === 'choice' ? (
                <>
                  <button
                    type="button"
                    onClick={handleNoShowNoRefundOption}
                    disabled={isNoShowProcessing}
                    className="inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium bg-red-600 contrast:bg-red-800 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    No Refund
                  </button>
                  <button
                    type="button"
                    onClick={handleNoShowRefundOption}
                    disabled={isNoShowProcessing}
                    className="inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Refund as Policy
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setNoShowState((prev) => ({
                        ...prev,
                        step: 'choice',
                        summary: null,
                        mode: null,
                      }))
                    }
                    disabled={isNoShowProcessing}
                    className="inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmRefundNoShow}
                    disabled={isNoShowProcessing}
                    className="inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Confirm No Show
                  </button>
                </>
              )}
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default BookingsListing;
